import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  Menu,
  X,
  ChevronDown,
  LogIn,
  User,
  UserCircle,
  Info,
} from "lucide-react";
import logo from "./logo.png";
import { useSelector, useDispatch } from "react-redux";
import { findMarqueee, findMyDetails, findNavs } from "../../utils/Api";
import { fetchWishlist } from "../../store/slice/whishlist";

const TOP_LINKS = [
  { label: "About Us", path: "/about", icon: Info },
  { label: "My Account", path: "/login", icon: UserCircle },
  { label: "Cart", path: "/Cart", icon: ShoppingCart },
];

const Header = () => {
  const dispatch = useDispatch();
  const { cart } = useSelector((state) => state.cart);

  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [token, setToken] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [navLinks, setNavLinks] = useState([]);

  useEffect(() => {
    findMarqueee()
      .then((res) => setAnnouncements(res.filter((i) => i.status !== false)))
      .catch((err) => console.error("Failed to load marquee:", err));

    findNavs()
      .then((response) => {
        const sorted = [...response].sort((a, b) => a.position - b.position);
        const formatted = sorted.map((cat) => ({
          label: cat.name,
          path: cat.SubCategory?.length > 0 ? null : getCategoryPath(cat?.slug, cat.name),
          dropdown:
            cat.SubCategory?.length > 0
              ? cat.SubCategory.map((sub) => ({
                label: sub.name,
                path: getSubCategoryPath(sub?.slug, cat.name, sub.name),
              }))
              : null,
        }));
        const defaultLinks = [

          {
            label: "Services",
            path:null,
            dropdown: [{
              path: "battery-installation",
              label: "Battery Insallation"
            },
            {
              path: "old-battery-exchange",
              label: "Old Battery Exchange"
            },
            {
              path: "warranty-support",
              label: "Warrenty Support"
            }]
          },
          {
            label: "Contact us",
            path: "/contact-us",

          }
        ]
        setNavLinks([...formatted, ...defaultLinks]);

      })
      .catch((err) => console.error("Failed to load nav:", err));
  }, []);

  useEffect(() => {
    setToken(!!sessionStorage.getItem("token_login"));
    dispatch(fetchWishlist());
    findMyDetails().catch(() => {
      sessionStorage.removeItem("token_login");
      setToken(false);
    });
  }, [dispatch]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const getCategoryPath = (id, name) =>
    `/Page/details/${id}`;

  const getSubCategoryPath = (id, subName) =>
    `/Page/details/sub/${id}`;

  return (
    <div className="w-full">
      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white text-sm overflow-hidden">
        <div className="animate-marquee flex whitespace-nowrap py-2">
          {[...announcements, ...announcements].map((item, i) => (
            <span key={i} className="mx-6 font-medium">
              {item.title}
            </span>
          ))}
        </div>
      </div>

      {/* Top Utility Bar - Desktop */}
      <div className="hidden lg:block bg-white border-b text-sm">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex gap-6 text-gray-700">
            {TOP_LINKS.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className="hover:text-red-600 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="text-gray-600">
            Need help? Call: <b>+91 78619 58315</b>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`bg-white sticky top-0 z-50 ${scrolled ? "shadow-md" : ""}`}
      >
        <div className="container mx-auto px-4">
          <div className="h-16 md:h-20 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden text-gray-700"
                onClick={() => setIsOpen(true)}
              >
                <Menu size={28} />
              </button>
              <Link to="/">
                <img src={logo} className="h-10 md:h-12" alt="Logo" />
              </Link>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex gap-8">
              {navLinks.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="font-medium hover:text-red-700 transition"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button className="flex items-center gap-1 font-medium hover:text-red-700 transition">
                      {item.label} <ChevronDown size={16} />
                    </button>
                  )}

                  {item.dropdown && activeDropdown === item.label && (
                    <div className="absolute top-full left-0 bg-white shadow-xl rounded-lg w-60 py-2 mt-1 border border-gray-100">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.path}
                          className="block px-5 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center gap-5 md:gap-6">
              <Link
                to="/cart"
                className="relative text-gray-700 hover:text-red-600 transition"
              >
                <ShoppingCart size={24} />
                {cart.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-white">
                    {cart.length}
                  </span>
                )}
              </Link>

              {token ? (
                <Link
                  to="/profile/dashboard"
                  className="text-gray-700 hover:text-red-600 transition"
                >
                  <User size={24} />
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-red-600 transition"
                >
                  <LogIn size={24} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${isOpen
          ? "opacity-100 visible"
          : "opacity-0 invisible pointer-events-none"
          }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsOpen(false)}
        />

        {/* Drawer Panel */}
        <div
          className={`absolute right-0 top-0 h-full w-[85%] max-w-[320px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
            } overflow-y-auto overscroll-y-contain`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b">
            <div className="flex items-center gap-3">
              <img src={logo} className="h-9" alt="Logo" />
              <span className="font-semibold text-lg">Menu</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <X size={28} />
            </button>
          </div>

          {/* Quick Links (Top Links) */}
          <div className="p-4 border-b bg-gray-50">
            <div className="grid grid-cols-2 gap-3">
              {TOP_LINKS.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg hover:bg-white active:bg-gray-100 transition text-gray-700"
                  >
                    <Icon size={22} />
                    <span className="text-xs font-medium text-center">
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Support Info */}
          <div className="px-5 py-4 border-b text-sm text-gray-600 bg-gray-50">
            Need help? Call us at
            <div className="font-semibold text-black mt-1">+91 78619 58315</div>
          </div>

          {/* Main Navigation */}
          <nav className="py-2">
            {navLinks.map((item) => (
              <div key={item.label} className="border-b last:border-b-0">
                {item.dropdown ? (
                  <>
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left font-medium hover:bg-gray-50 active:bg-gray-100 transition"
                      onClick={() =>
                        setMobileDropdown(
                          mobileDropdown === item.label ? null : item.label,
                        )
                      }
                    >
                      {item.label}
                      <ChevronDown
                        className={`transition-transform duration-200 ${mobileDropdown === item.label ? "rotate-180" : ""
                          }`}
                        size={20}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ${mobileDropdown === item.label ? "max-h-96" : "max-h-0"
                        }`}
                    >
                      <div className="bg-gray-50 py-2">
                        {item.dropdown.map((sub) => (
                          <Link
                            key={sub.label}
                            to={sub.path}
                            className="block px-5 py-3 pl-9 text-gray-700 hover:bg-white hover:text-red-600 active:bg-gray-100 transition"
                            onClick={() => setIsOpen(false)}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className="block px-5 py-4 font-medium hover:bg-gray-50 active:bg-gray-100 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Header;
